// utils/streamSSE.ts
// Async iterator wrapping fetch + ReadableStream parse for text/event-stream responses.

export interface SseMessage {
  event: string;
  data: unknown;  // JSON.parsed from the data line
}

export interface StreamSseOptions {
  method?: 'GET' | 'POST';
  headers?: Record<string, string>;
  body?: string;
  signal?: AbortSignal;
  /** Runs immediately after fetch resolves and before generic HTTP validation. */
  onResponse?: (response: Response) => void | Promise<void>;
}

export async function* streamSSE(url: string, opts: StreamSseOptions = {}): AsyncGenerator<SseMessage, void, unknown> {
  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers: { Accept: 'text/event-stream', ...opts.headers },
    body: opts.body,
    signal: opts.signal,
  });
  await opts.onResponse?.(res);
  if (!res.ok || !res.body) {
    throw new Error(`SSE fetch failed: ${res.status} ${res.statusText}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // Process complete events (\n\n delimited)
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const parsed = parseSseChunk(rawEvent);
      if (parsed) yield parsed;
    }
  }
  // Flush any trailing partial if it's a complete event (should be rare)
  if (buffer.trim()) {
    const parsed = parseSseChunk(buffer);
    if (parsed) yield parsed;
  }
}

function parseSseChunk(raw: string): SseMessage | null {
  const lines = raw.split(/\r?\n/);
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
  }
  if (dataLines.length === 0) return null;
  const dataRaw = dataLines.join('\n');
  let data: unknown = dataRaw;
  try { data = JSON.parse(dataRaw); } catch { /* keep as string */ }
  return { event, data };
}
